// frontend/src/components/common/FormField.jsx
import React from 'react';
import './FormField.scss';

/**
 * 共通フォームフィールドコンポーネント
 * 
 * @param {string} label - フィールドのラベル
 * @param {string} name - フィールド名（id, nameに使用）
 * @param {string} type - input type（text, number, email, tel, date等）
 * @param {string|number} value - フィールドの値
 * @param {function} onChange - 値変更時のハンドラ (name, value) => void
 * @param {function} onBlur - フォーカスアウト時のハンドラ (name) => void
 * @param {function} onCompositionStart - IME変換開始時のハンドラ
 * @param {function} onCompositionEnd - IME変換終了時のハンドラ (name, value) => void
 * @param {string} error - エラーメッセージ
 * @param {boolean} touched - フィールドがタッチされたか
 * @param {boolean} required - 必須フィールドかどうか
 * @param {boolean} optional - 任意フィールドかどうか（明示的に表示したい場合）
 * @param {string} placeholder - プレースホルダー
 * @param {boolean} disabled - 無効化するか
 * @param {string} as - レンダリングする要素（'input', 'textarea', 'select'）
 * @param {React.ReactNode} children - selectの場合のoption要素
 * @param {string} helperText - ヘルパーテキスト（フィールドの説明）
 * @param {number} rows - textareaの行数
 * @param {number} maxLength - 最大文字数
 * @param {boolean} showCharCount - 文字数カウンターを表示するか
 */
const FormField = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  onCompositionStart,
  onCompositionEnd,
  error,
  touched,
  required = false,
  optional = false,
  placeholder,
  disabled = false,
  as = 'input',
  children,
  helperText,
  rows = 3,
  maxLength,
  showCharCount = false,
  ...props
}) => {
  const showError = touched && error;
  const Component = as;
  
  // 値の文字数
  const charCount = value ? String(value).length : 0;

  const handleChange = (e) => {
    if (onChange) {
      onChange(name, e.target.value);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur(name);
    }
  };

  const handleCompositionStart = () => {
    if (onCompositionStart) {
      onCompositionStart(name);
    }
  };

  const handleCompositionEnd = (e) => {
    if (onCompositionEnd) {
      onCompositionEnd(name, e.target.value);
    }
  };

  return (
    <div className={`mc-form-field ${showError ? 'mc-form-field--error' : ''}`}>
      {label && (
        <label htmlFor={name} className="mc-form-field__label">
          {label}
          {required && <span className="mc-form-field__required">※必須</span>}
          {optional && <span className="mc-form-field__optional">任意</span>}
        </label>
      )}
      
      {helperText && (
        <div className="mc-form-field__helper">{helperText}</div>
      )}
      
      <Component
        id={name}
        name={name}
        type={type}
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        placeholder={placeholder}
        disabled={disabled}
        rows={as === 'textarea' ? rows : undefined}
        maxLength={maxLength}
        className={`mc-form-field__input ${showError ? 'mc-form-field__input--invalid' : ''}`}
        aria-invalid={showError ? 'true' : 'false'}
        aria-describedby={showError ? `${name}-error` : undefined}
        aria-required={required ? 'true' : 'false'}
        {...props}
      >
        {children}
      </Component>
      
      {showCharCount && maxLength && (
        <div className="mc-form-field__char-count">
          {charCount} / {maxLength}文字
        </div>
      )}
      
      {showError && (
        <div 
          id={`${name}-error`} 
          className="mc-form-field__error" 
          role="alert"
        >
          ⚠ {error}
        </div>
      )}
    </div>
  );
};

export default FormField;


